import type { Affiliation } from "@/api/affiliations";

/**
 * Affichage non intrusif des liens d'affiliation (F-MON-003, DGCCRF).
 *
 * Conventions :
 *   - Chaque lien porte une mention "Lien partenaire" lisible.
 *   - Les liens officiels (France Rénov', ADEME) sont marqués distinctement.
 *   - target="_blank" + rel="noopener noreferrer sponsored" pour SEO/sécu.
 *   - Aucune pop-up ni interstitielle.
 */
export function AffiliationLinks({
  items,
  title = "Pour aller plus loin",
}: {
  items: Affiliation[] | undefined;
  title?: string;
}) {
  if (!items || items.length === 0) return null;
  return (
    <div className="mt-4">
      <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-600">
        {title}
      </h4>
      <ul className="mt-2 space-y-2">
        {items.map((a) => (
          <li key={a.partner_id}>
            <AffiliationItem item={a} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function AffiliationItem({ item }: { item: Affiliation }) {
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer sponsored"
      className="flex items-start gap-3 rounded border border-gray-200 bg-white p-3 hover:border-blue-700 hover:shadow"
    >
      <div className="flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{item.label}</span>
          {item.is_official ? (
            <span className="rounded bg-blue-50 px-1.5 py-0.5 text-xs font-medium text-blue-900">
              Officiel
            </span>
          ) : (
            <span
              className="rounded bg-gray-100 px-1.5 py-0.5 text-xs text-gray-600"
              title="Ce lien permet à l'éditeur de percevoir une commission, sans surcoût pour vous."
            >
              {item.disclosure_label}
            </span>
          )}
        </div>
        {item.description && (
          <p className="mt-1 text-xs text-gray-600">{item.description}</p>
        )}
      </div>
      <span
        aria-hidden="true"
        className="rounded bg-blue-900 px-3 py-1 text-xs text-white"
      >
        {item.cta}
      </span>
    </a>
  );
}
